/**
 * 계약서의 값 타입. **행 인터페이스(`Contract`)는 여기 없다** — 저장소(묶음 5)가
 * 붙인다. 지금은 금액·전이·설정 기본값이 공유하는 상수만 둔다.
 *
 * 값은 전부 `0019_contract.sql` 의 check 와 같은 목록이다. 한쪽만 늘리면 앱이
 * 보내는 값을 DB 가 거절하거나(그나마 낫다) DB 에 앱이 모르는 값이 남는다.
 */

/** 계약 상태 6개. 전이는 `modules/contract/transitions.ts` 가 정한다. */
export const CONTRACT_STATUSES = [
  "DRAFT",
  "SENT",
  "SIGNED",
  "VOID",
  "TERMINATED",
  "COMPLETED",
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = {
  DRAFT: "작성 중",
  SENT: "발송",
  SIGNED: "체결",
  VOID: "파기",
  TERMINATED: "해지",
  COMPLETED: "준공",
};

/**
 * 누가 체결을 눌렀나.
 *
 * `CUSTOMER` 는 고객이 링크에서 직접 누른 것이고 IP·UA 가 증거다. `STAFF` 는
 * 담당자가 오프라인 날인을 **대신 기록**한 것이라 IP·UA 가 증거가 못 된다
 * — 그쪽 증거는 `contract_file` 의 스캔본이다(`0019` 의 두 check).
 */
export const CONTRACT_SIGNERS = ["CUSTOMER", "STAFF"] as const;
export type ContractSigner = (typeof CONTRACT_SIGNERS)[number];

/**
 * 조항의 출처. **우리가 쓰지 않는다**(노트 `12`) — 공정위 표준약관 제10079호가
 * 기본이고 국토부 고시가 선택이다. 회사가 한 글자라도 고치면 그 계약 사본만
 * `CUSTOM` 이 되고 「공정위 표준」 표지를 뗀다.
 */
export const CLAUSE_SOURCES = ["FTC_10079", "MOLIT", "CUSTOM"] as const;
export type ClauseSource = (typeof CLAUSE_SOURCES)[number];

export const CLAUSE_SOURCE_LABEL: Record<ClauseSource, string> = {
  FTC_10079: "공정거래위원회 표준약관 제10079호",
  MOLIT: "국토교통부 민간건설공사 표준도급계약서",
  CUSTOM: "회사 자체 조항",
};

/** 계약서에 실리는 조항 한 개. `contract.clauses` jsonb 의 원소다. */
export interface ContractClause {
  /** 목록에서의 자리(1부터). 정렬과 편집에 쓴다. */
  no: number;
  /**
   * 원문의 조 번호 표기. 없으면 인쇄가 `제{no}조` 로 쓴다.
   *
   * **`no` 로 대신할 수 없다** — 국토부 일반조건에 `제22조의2` 가 있어 가지번호가
   * 존재한다. 숫자로만 세면 그 조부터 원문과 번호가 한 칸씩 어긋난다.
   */
  label?: string;
  title: string;
  body: string;
}

/**
 * 공종별 하자담보 기간의 **예외**. 일반 기간은 `contract.warranty_months` 다.
 *
 * 공정위 제10조가 실내건축 1년 · 창호 2년(유리는 1년)으로 나눠 놓았다 — 한 칸으로
 * 담으면 샷시가 낀 현장에서 본문과 표지가 어긋난다.
 */
export interface WarrantyTerm {
  /** 공종 이름. 코드가 아니라 계약서에 찍히는 말이다(「창호」·「유리」·「방수」). */
  label: string;
  months: number;
}

/**
 * 특약 한 개. **조항(`ContractClause`)과 다른 칸이다** — 표준 조항에 끼워 넣으면
 * 본문이 원문과 달라져 「공정위 표준」 표지가 떨어진다(`clauseSourceAfterEdit`).
 * 원문이 담지 않는 것(인지세 분담·관리사무소 비용·이웃 손해)이 여기로 간다.
 */
export interface SpecialTerm {
  no: number;
  title: string;
  body: string;
}

/** 지급회차 한 개 중 **계산이 보는 부분**. 행 전체는 저장소가 안다. */
export interface PaymentPlanRow {
  seq: number;
  label: string;
  /** 만분율. `> 0` · 계약 때 합 10000(`assertPaymentRatios`) — 체결 뒤 변경공사 정산 회차가 붙으면 넘을 수 있다. */
  ratio_bp: number;
  /** 무엇이 끝나면 받는가. **필수다** — 「도기세팅 또는 타일 완료」가 실물이다. */
  trigger_text: string;
}
